import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  IModerationCaseDetail,
  IModerationCaseRow,
  IModerationSummary,
  ModerationCaseFilter,
  PostReportReason,
  POST_REPORT_REASONS,
} from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { Config } from "@eagerdog/config";
import moment from "moment";

import { type ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";
import { Modal } from "../../../components/Modal/Modal";
import Button from "../../../components/Button/Button";
import Dropdown, { IOption } from "../../../components/Dropdown/Dropdown";
import Checkbox from "../../../components/Checkbox/Checkbox";
import TextArea from "../../../components/TextArea/TextArea";
import RunSummary from "../../Discover/Feed/FeedItem/RunSummary";

import styles from "./ModerationTab.module.scss";

interface IProps {
  currentTab: number;
  tabIndex: number;
  /** Lets the Admin shell refresh its sidebar badge after an action. */
  onSummaryChange?: (summary: IModerationSummary) => void;
}

const REASON_LABEL = Constants.post_report_reason_label as Record<PostReportReason, string>;
// Same default as the API (config is shared); the server enforces the real value.
const SUSPENSION_DAYS = Config.eagerdog_api.moderation.suspension_days;

const FILTERS: { id: ModerationCaseFilter; label: string; countKey?: keyof IModerationSummary }[] = [
  { id: "needs_review", label: "Needs review", countKey: "needs_review" },
  { id: "under_review", label: "Under review", countKey: "under_review" },
  { id: "flagged", label: "Flagged by screening", countKey: "flagged" },
  { id: "unscreened", label: "Unscreened", countKey: "unscreened" },
  { id: "resolved", label: "Resolved" },
];

const STATUS_LABEL: Record<string, string> = {
  visible: "Visible",
  under_review: "Under review",
  removed: "Removed",
};

const postTypeLabel = (type: string) => type.replace(/_/g, " ");

// ---------------- Detail modal ----------------

interface ICaseModalProps {
  postId: string;
  onClose: () => void;
  onActed: () => void;
}

const CaseModal: React.FC<ICaseModalProps> = ({ postId, onClose, onActed }) => {
  const [detail, setDetail] = useState<IModerationCaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<"view" | "remove">("view");
  const [reason, setReason] = useState<IOption | null>(null);
  const [note, setNote] = useState("");
  const [suspend, setSuspend] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reasonOptions: IOption[] = useMemo(
    () => POST_REPORT_REASONS.map((r) => ({ id: r, value: REASON_LABEL[r] })),
    []
  );

  const load = useCallback(() => {
    setLoading(true);
    apiService.getModerationCase(postId)
      .then((d) => {
        setDetail(d);
        // Default the removal reason to the most-reported one.
        const top = d.reports[0]?.reason;
        if (top) setReason({ id: top, value: REASON_LABEL[top] });
      })
      .catch(() => toast.show({ title: "Error", content: "Failed to load case", duration: 5000, type: "fail" }))
      .finally(() => setLoading(false));
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const run = async (action: () => Promise<unknown>, successTitle: string) => {
    setSubmitting(true);
    try {
      await action();
      toast.show({ title: successTitle, content: "", duration: 4000, type: "success" });
      onActed();
      onClose();
    } catch (err: any) {
      toast.show({
        title: "Action failed",
        content: err?.response?.data?.message || "Please try again.",
        duration: 6000,
        type: "fail",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const post = detail?.post;
  const status = post?.moderation_status || "visible";

  return (
    <Modal
      className="moderationCaseModal"
      isShown={true}
      hide={onClose}
      modalContent={
        <div className={styles.caseWrap}>
          <div className={styles.caseHeader}>
            <h2 className={styles.caseTitle}>Review post</h2>
            {post && <span className={`${styles.statusPill} ${styles[status]}`}>{STATUS_LABEL[status]}</span>}
          </div>

          {loading && <div className={styles.muted}>Loading…</div>}

          {post && (
            <>
              <section className={styles.postPreview}>
                <div className={styles.previewHeader}>
                  <div className={styles.avatar}>
                    {(post.dog_image_url || post.author_image_url)
                      ? <img src={post.dog_image_url || post.author_image_url} alt="" />
                      : <span>{(post.dog_call_name || post.author_display_name || "?").charAt(0).toUpperCase()}</span>}
                  </div>
                  <div className={styles.previewText}>
                    <div className={styles.previewAuthor}>
                      {post.author_display_name || "Unknown"}
                      <span className={styles.muted}> · {postTypeLabel(post.post_type)}{post.dog_call_name ? ` · ${post.dog_call_name}` : ""}</span>
                    </div>
                    <div className={styles.muted}>
                      {moment(post.created_at).format("MMM D, YYYY h:mm A")}
                      {detail?.author_email ? ` · ${detail.author_email}` : ""}
                    </div>
                  </div>
                </div>
                {post.run_summary && <RunSummary run={post.run_summary} />}
                {post.body && <div className={styles.previewBody}>{post.body}</div>}
                {post.media?.[0] && (
                  <div className={styles.previewMedia}>
                    {post.media[0].type === Constants.post_media_type.video
                      ? <video src={post.media[0].url} controls />
                      : <img src={post.media[0].url} alt="" />}
                  </div>
                )}
              </section>

              <div className={styles.columns}>
                <section>
                  <h3>Reports ({detail!.reports.length})</h3>
                  {detail!.reports.length === 0 && <div className={styles.muted}>None</div>}
                  <ul className={styles.list}>
                    {detail!.reports.map((r) => (
                      <li key={r._id}>
                        <div className={styles.listTop}>
                          <strong>{REASON_LABEL[r.reason]}</strong>
                          <span className={styles.muted}>{moment(r.created_at).fromNow()}</span>
                        </div>
                        <div className={styles.muted}>
                          {r.reporter_display_name || "Unknown"} · account {r.reporter_account_age_days}d old
                          {r.status === "resolved" ? ` · ${r.resolution}` : ""}
                        </div>
                        {r.note && <div className={styles.note}>“{r.note}”</div>}
                      </li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h3>Screening</h3>
                  {!post.moderation?.screening?.length && <div className={styles.muted}>Not screened</div>}
                  <ul className={styles.list}>
                    {(post.moderation?.screening || []).map((s, i) => (
                      <li key={i}>
                        <div className={styles.listTop}>
                          <strong>{s.provider}</strong>
                          <span className={`${styles.screenStatus} ${styles[s.status]}`}>{s.status}</span>
                        </div>
                        {s.labels?.length ? (
                          <div className={styles.labels}>
                            {s.labels.map((l) => (
                              <span key={l.name} className={styles.label}>
                                {l.parent ? `${l.parent} › ` : ""}{l.name} <em>{l.confidence}%</em>
                              </span>
                            ))}
                          </div>
                        ) : null}
                        {s.error && <div className={styles.muted}>{s.error}</div>}
                      </li>
                    ))}
                  </ul>

                  <h3>History</h3>
                  <ul className={styles.list}>
                    {detail!.actions.map((a) => (
                      <li key={a._id}>
                        <div className={styles.listTop}>
                          <strong>{a.action.replace(/_/g, " ")}</strong>
                          <span className={styles.muted}>{moment(a.created_at).format("MMM D, h:mm A")}</span>
                        </div>
                        <div className={styles.muted}>
                          {a.actor_display_name || "System"}
                          {a.reason ? ` · ${REASON_LABEL[a.reason as PostReportReason] || a.reason}` : ""}
                        </div>
                        {a.note && <div className={styles.note}>{a.note}</div>}
                      </li>
                    ))}
                  </ul>
                </section>
              </div>

              {detail?.author_posting_suspended_until && new Date(detail.author_posting_suspended_until) > new Date() && (
                <div className={styles.notice}>
                  Author's posting is paused until {moment(detail.author_posting_suspended_until).format("MMM D, YYYY")}
                  {detail.author_moderation_strikes ? ` · ${detail.author_moderation_strikes} strike${detail.author_moderation_strikes === 1 ? "" : "s"}` : ""}.
                </div>
              )}

              {mode === "remove" && (
                <section className={styles.removeForm}>
                  <Dropdown
                    label="Reason"
                    placeholder="Choose a reason"
                    options={reasonOptions}
                    value={reason || { id: "", value: "" }}
                    onChange={(_e, option) => setReason(option)}
                  />
                  <TextArea
                    label="Internal note (optional)"
                    placeholder=""
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 1000))}
                  />
                  {post.author_type === Constants.post_author_type.user && (
                    <Checkbox
                      id="moderation-suspend"
                      label={`Also pause the author's posting for ${SUSPENSION_DAYS} days`}
                      value={suspend}
                      onChange={() => setSuspend((v) => !v)}
                    />
                  )}
                </section>
              )}

              <div className={styles.caseFooter}>
                <Button variant="secondary" onClick={onClose} disabled={submitting}>Close</Button>
                <div className={styles.spacer} />
                {status === "removed" ? (
                  <Button
                    isLoading={submitting}
                    onClick={() => run(() => apiService.moderationRestore(postId, { note: note || undefined }), "Post restored")}
                  >
                    Restore post
                  </Button>
                ) : mode === "view" ? (
                  <>
                    <Button
                      variant="secondary"
                      isLoading={submitting}
                      onClick={() => run(() => apiService.moderationDismiss(postId, {}), "Reports dismissed")}
                    >
                      Dismiss
                    </Button>
                    <Button className={styles.danger} onClick={() => setMode("remove")} disabled={submitting}>
                      Remove post…
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" onClick={() => setMode("view")} disabled={submitting}>Back</Button>
                    <Button
                      className={styles.danger}
                      isLoading={submitting}
                      disabled={!reason?.id}
                      onClick={() =>
                        run(
                          () =>
                            apiService.moderationRemove(postId, {
                              reason: reason!.id as PostReportReason,
                              note: note || undefined,
                              suspend_author: suspend,
                            }),
                          suspend ? "Post removed and author paused" : "Post removed"
                        )
                      }
                    >
                      {suspend ? "Remove and pause posting" : "Remove post"}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      }
    />
  );
};

// ---------------- Tab ----------------

const ModerationTab: React.FC<IProps> = ({ currentTab, tabIndex, onSummaryChange }) => {
  const [filter, setFilter] = useState<ModerationCaseFilter>("needs_review");
  const [rows, setRows] = useState<IModerationCaseRow[]>([]);
  const [summary, setSummary] = useState<IModerationSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const theme = themeQuartz;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cases, sum] = await Promise.all([
        apiService.getModerationCases(filter),
        apiService.getModerationSummary(),
      ]);
      setRows(cases);
      setSummary(sum);
      onSummaryChange?.(sum);
    } catch (err) {
      console.error("Failed to load moderation queue:", err);
      toast.show({ title: "Error", content: "Failed to load moderation queue", duration: 5000, type: "fail" });
    } finally {
      setLoading(false);
    }
  }, [filter, onSummaryChange]);

  useEffect(() => {
    if (currentTab === tabIndex) load();
  }, [currentTab, tabIndex, load]);

  const colDefs: ColDef<IModerationCaseRow>[] = [
    {
      headerName: "Reported",
      field: "last_reported_at",
      minWidth: 130,
      sort: "desc",
      valueFormatter: (p) => (p.value ? moment(p.value).fromNow() : "—"),
    },
    {
      headerName: "Post",
      minWidth: 280,
      flex: 2,
      valueGetter: (p) => p.data,
      cellRenderer: (p: any) => {
        const r = p.data as IModerationCaseRow;
        return (
          <span className={styles.postCell}>
            <span className={styles.typePill}>{postTypeLabel(r.post_type)}</span>
            <strong>{r.author_display_name || "Unknown"}</strong>
            {r.dog_call_name ? ` · ${r.dog_call_name}` : ""}
            <span className={styles.muted}>{r.body_preview ? ` — ${r.body_preview}` : r.has_media ? " — Photo" : ""}</span>
          </span>
        );
      },
    },
    {
      headerName: "Reports",
      field: "report_count",
      minWidth: 100,
      type: "numericColumn",
      tooltipValueGetter: (p) => (p.data?.reasons || []).map((r) => REASON_LABEL[r]).join(", "),
    },
    {
      headerName: "Top reason",
      field: "top_reason",
      minWidth: 180,
      valueFormatter: (p) => (p.value ? REASON_LABEL[p.value as PostReportReason] : "—"),
    },
    { headerName: "Screening", field: "screening_summary", minWidth: 170 },
    {
      headerName: "Status",
      field: "moderation_status",
      minWidth: 130,
      cellRenderer: (p: any) => (
        <span className={`${styles.statusPill} ${styles[p.value]}`}>{STATUS_LABEL[p.value] || p.value}</span>
      ),
    },
    {
      headerName: "",
      minWidth: 110,
      maxWidth: 120,
      sortable: false,
      cellRenderer: (p: any) => (
        <span className={styles.reviewLink} onClick={() => setSelected(p.data.post_id)}>Review</span>
      ),
    },
  ];

  if (currentTab !== tabIndex) return null;

  return (
    <div className={styles.ModerationTab}>
      <div className={styles.filters} role="tablist">
        {FILTERS.map((f) => {
          const count = f.countKey && summary ? summary[f.countKey] : undefined;
          return (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={`${styles.filter} ${filter === f.id ? styles.active : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              {typeof count === "number" && <span className={styles.count}>{count}</span>}
            </button>
          );
        })}
      </div>

      <div className={styles.tableWrap}>
        <div className={`ag-theme-quartz ${styles.grid}`}>
          <AgGridReact<IModerationCaseRow>
            theme={theme}
            rowData={rows}
            columnDefs={colDefs}
            defaultColDef={{ resizable: true, sortable: true, flex: 1 }}
            loading={loading}
            tooltipShowDelay={300}
            onRowDoubleClicked={(e) => e.data && setSelected(e.data.post_id)}
            overlayLoadingTemplate="<span style='padding: 20px; font-size: 14px; color: #666;'>Loading…</span>"
            overlayNoRowsTemplate="<span style='padding: 20px; font-size: 16px; color: #666;'>Nothing to review</span>"
          />
        </div>
      </div>

      {selected && (
        <CaseModal postId={selected} onClose={() => setSelected(null)} onActed={load} />
      )}
    </div>
  );
};

export default ModerationTab;
